import { Skeleton, cn } from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { renderCell } from './cellRenderers';
import { EmptyLogsView, Header, LogsTableStats } from './components';
import { TABLE_COLUMNS } from './constants';
import {
  pipelineLogsQueryKeys,
  useHasActiveFilters,
  usePipelineLogsData,
  usePipelineLogsFilters,
  useRetentionInfo,
  useTableSort,
  useTimeRangeCalculation
} from './hooks';
import {
  isOTELLog,
  mapOtelToLogSearch,
  type PipelineLog,
  type PipelineLogOtel
} from './types';

import { MONITOR_PAGE_SIZE } from '~/api/monitorService';
import featureFlagConstants from '~/common/helpers/featureFlagConstants';
import i18n from '~/common/helpers/i18n';
import useFeatureFlag from '~/common/hooks/useFeatureFlag';
import { Table } from '~/components/Table/Table';
import { LogSearch, useLogSearchLazyQuery } from '~/generated/graphql';
import type { StoreRootState } from '~/scenes/Governance/types/StoreRootState';
import { usePipelinesAndVersionAutocomplete } from '~/scenes/Monitor/hooks';
import LogDetails from '~/scenes/Monitor/scenes/OldPipelineLogs/components/LogDetails';

type PipelineLogsProps = {
  embedded?: boolean;
};

export function PipelineLogs({ embedded = false }: PipelineLogsProps) {
  const [selectedLog, setSelectedLog] = useState<LogSearch | null>(null);
  const [isLogDetailsOpen, setIsLogDetailsOpen] = useState(false);

  const { realm, environment, environments } = useSelector(
    (state: StoreRootState) => ({
      realm: state.application?.realm.realm,
      environment: state.application?.environment,
      environments: state.application?.environments?.byName
    })
  );

  const queryClient = useQueryClient();
  const retentionInfo = useRetentionInfo(environment, environments);

  const { treatments: autocompleteFlag } = useFeatureFlag([
    featureFlagConstants.MONITOR_PIPELINE_LOGS_AUTOCOMPLETE_FILTERS
  ]);
  const isAutocompleteEnabled =
    autocompleteFlag[
      featureFlagConstants.MONITOR_PIPELINE_LOGS_AUTOCOMPLETE_FILTERS
    ]?.treatment === 'on';

  const filters = usePipelineLogsFilters();
  const timeRange = useTimeRangeCalculation(filters.timeState);

  const selectedPipelineName = filters.form.values.pipelineName || null;

  const autocomplete = usePipelinesAndVersionAutocomplete({
    realm,
    enabled: isAutocompleteEnabled && !!realm,
    selectedPipelineName
  });

  const searchParams = useMemo(
    () => ({
      ...filters.urlParams,
      time: timeRange.apiTime,
      specific: timeRange.specificDates
    }),
    [filters.urlParams, timeRange.apiTime, timeRange.specificDates]
  );

  const { logs, count, loading, loadingMore, hasMore, error, handleLoadMore } =
    usePipelineLogsData({
      realm,
      environment,
      searchParams
    });

  const [
    fetchLogDetails,
    { loading: isLogDetailsLoading, error: logDetailsError }
  ] = useLogSearchLazyQuery({
    onCompleted: data => {
      setSelectedLog(data?.logSearch ?? null);
    }
  });

  const handleRowClick = useCallback(
    (log: PipelineLog | PipelineLogOtel) => {
      setIsLogDetailsOpen(true);

      if (isOTELLog(log)) {
        const logSearch = mapOtelToLogSearch(log);
        setSelectedLog(logSearch);
      } else {
        fetchLogDetails({
          variables: {
            realm,
            environment,
            id: log.id
          }
        });
      }
    },
    [environment, fetchLogDetails, realm]
  );

  const handleCloseLogDetails = useCallback(() => {
    setIsLogDetailsOpen(false);
    setSelectedLog(null);
  }, []);

  const { sortDescriptor, handleSortChange } = useTableSort({
    direction: filters.urlParams.direction,
    onDirectionChange: filters.form.setters.setDirection
  });

  const hasFilters = useHasActiveFilters({
    filters: filters.urlParams,
    time: searchParams.time
  });

  const autoProps = isAutocompleteEnabled
    ? {
        isAutocompleteEnabled: true as const,
        pipelineItems: autocomplete.pipelineItems,
        pipelineHasMore: autocomplete.pipelineHasMore,
        isPipelineLoading: autocomplete.isPipelineLoading,
        isPipelineError: autocomplete.isPipelineError,
        onPipelineSearch: autocomplete.onPipelineSearch,
        onPipelineLoadMore: autocomplete.onPipelineLoadMore,
        onPipelineNameSubmit: filters.handlePipelineNameSubmit,
        versionItems: autocomplete.versionItems,
        versionHasMore: autocomplete.versionHasMore,
        isVersionLoading: autocomplete.isVersionLoading,
        isVersionError: autocomplete.isVersionError,
        onVersionSearch: autocomplete.onVersionSearch,
        onVersionLoadMore: autocomplete.onVersionLoadMore,
        onVersionSubmit: filters.handleVersionSubmit
      }
    : { isAutocompleteEnabled: false as const };

  const handleSearch = useCallback(() => {
    filters.handleSearch();
    const { dateTo } = filters.urlParams;
    if (dateTo && new Date(dateTo) > new Date()) {
      queryClient.invalidateQueries({
        queryKey: pipelineLogsQueryKeys.lists()
      });
    }
  }, [filters, queryClient]);

  return (
    <div
      className={cn('flex flex-col overflow-auto', !embedded && 'p-10')}
      data-testid={embedded ? 'pipeline-logs-embedded' : undefined}
    >
      <Header
        {...filters.form.values}
        timeRange={timeRange.currentTimeRange}
        resetToDefault={filters.timeState.resetToDefault}
        retentionValue={retentionInfo.value}
        onPipelineNameChange={filters.form.setters.setPipelineName}
        onPipelineVersionChange={filters.form.setters.setPipelineVersion}
        onLogMessageChange={filters.form.setters.setLogMessage}
        onPipelineKeyChange={filters.form.setters.setPipelineKey}
        onLevelChange={filters.form.setters.setLevel}
        onLogTypeChange={filters.form.setters.setLogType}
        onTimeRangeChange={filters.handleTimeRangeChange}
        onSearch={handleSearch}
        onClear={filters.handleClear}
        {...autoProps}
      />

      <div className='mt-10'>
        {error && (
          <div
            className='text-error py-12 text-center'
            data-testid='monitor-pipeline-logs-message-error'
          >
            Error loading logs: {error.message}
          </div>
        )}

        {!error && (
          <div className='overflow-hidden'>
            {loading && (
              <div
                className='mb-2.5 flex flex-col gap-0.5 pl-4'
                data-testid='monitor-pipeline-logs-skeleton-loader'
              >
                <Skeleton className='h-6 w-64 rounded' />
                <Skeleton className='h-5 w-40 rounded' />
              </div>
            )}

            {!loading && (
              <LogsTableStats
                count={count}
                retentionValue={retentionInfo.value}
                retentionUnit={retentionInfo.unit}
              />
            )}

            {!loading && logs.length === 0 ? (
              <EmptyLogsView
                hasFilters={hasFilters}
                onClear={filters.handleClear}
              />
            ) : (
              <Table
                ariaLabel='pipeline-logs-table'
                data-testid='monitor-pipeline-logs-table-list'
                data={logs}
                hideHeader={false}
                columns={TABLE_COLUMNS}
                rowsPerPage={MONITOR_PAGE_SIZE}
                isLoading={loading}
                onRowClick={handleRowClick}
                renderCell={renderCell}
                useFlexLayout={false}
                variant='borderless'
                sortDescriptor={sortDescriptor}
                onSortChange={handleSortChange}
                infiniteScroll={{
                  hasMore,
                  isLoadingMore: loadingMore,
                  onLoadMore: handleLoadMore,
                  loadMoreText: String(
                    i18n.t('scenes.pipeline_logs.actions.more_logs')
                  )
                }}
              />
            )}
          </div>
        )}
      </div>

      <LogDetails
        open={isLogDetailsOpen}
        close={handleCloseLogDetails}
        loading={isLogDetailsLoading}
        log={selectedLog}
        error={logDetailsError}
      />
    </div>
  );
}
